// Master-Hotfix P0.2 (2026-07) — SINGLE source of truth for the AI
// workspace viewport. One layout contract, one keyboard state, zero
// competing hooks:
//
//   --ai-vvh   = visualViewport.height   ONLY while the keyboard is open
//                (otherwise removed → CSS falls back to 100dvh, so the
//                browser natively animates toolbar collapse/expand)
//   --ai-vvoff = visualViewport.offsetTop ONLY while the keyboard is open
//                (iOS Safari pans the visual viewport over the layout
//                viewport to reveal the focused input — the workspace
//                FOLLOWS that pan via `top`, it never fights it)
//   html.ai-kb-open  → bottom tab bar hidden (Option A), composer sits
//                directly above the keyboard
//
// Why the previous fix failed on real iOS (root cause of the P0
// regression): visualViewport `scroll` events after the open/close
// transition were deliberately ignored ("never fight user pans"), so
// `--ai-vvoff` kept a STALE value (usually 0) while Safari finished its
// focus pan. The fixed container stayed at layout-y 0..vvh while the
// visible window showed layout-y offsetTop..innerHeight → the composer
// (bottom of the container) left the visible area and the user saw
// black space + keyboard. Additionally `transform: translateY(...)`
// made the container a containing block, so the `position: fixed`
// bottom nav anchored to the container instead of the viewport.
//
// Fix: while the keyboard is open a requestAnimationFrame loop mirrors
// vv.height/offsetTop into the CSS variables every frame (writes only
// on change) — no event-timing games, no stale values, no scrollTo, no
// transform. Keyboard detection covers BOTH mobile keyboard models:
//   overlay (iOS classic): innerHeight - vv.height > threshold
//   resize  (Android / interactive-widget): text input focused AND
//            vv.height dropped > threshold below the no-focus baseline.
import { useEffect } from "react";

const KB_DELTA = 120; // px — smaller deltas are browser-toolbar changes

const isTextTarget = (el) =>
  !!el &&
  (el.tagName === "TEXTAREA" ||
    el.tagName === "INPUT" ||
    el.isContentEditable === true);

export function useAiWorkspaceViewport(active) {
  useEffect(() => {
    if (!active) return undefined;
    const root = document.documentElement;
    // Structural scroll ownership: the document CANNOT scroll on /ai.
    root.classList.add("ai-ws-lock");
    const vv = window.visualViewport;
    if (!vv) return () => root.classList.remove("ai-ws-lock");

    let sim = null; // VisualViewport test harness (Playwright)
    let rafId = 0;
    let loopRunning = false;
    let textFocused = false;
    let baselineH = vv.height; // last known height WITHOUT keyboard
    const last = { h: -1, t: -1, kb: false };

    const read = () =>
      sim || {
        height: vv.height,
        offsetTop: vv.offsetTop,
        innerHeight: window.innerHeight,
      };

    const measure = () => {
      const { height, offsetTop, innerHeight } = read();
      const overlayKb = innerHeight - height > KB_DELTA;
      const resizeKb = textFocused && baselineH - height > KB_DELTA;
      const kb = overlayKb || resizeKb;
      if (!kb && !textFocused) baselineH = height;

      if (kb !== last.kb) {
        last.kb = kb;
        root.classList.toggle("ai-kb-open", kb);
      }
      if (kb) {
        const h = Math.round(height);
        const t = Math.max(0, Math.round(offsetTop));
        if (h !== last.h) {
          last.h = h;
          root.style.setProperty("--ai-vvh", `${h}px`);
        }
        if (t !== last.t) {
          last.t = t;
          root.style.setProperty("--ai-vvoff", `${t}px`);
        }
      } else if (last.h !== -1 || last.t !== -1) {
        last.h = -1;
        last.t = -1;
        root.style.removeProperty("--ai-vvh"); // fallback: 100dvh
        root.style.removeProperty("--ai-vvoff");
      }

      // iOS finishes its focus pan AFTER the resize event and without
      // reliable scroll events — mirror the viewport per frame while
      // the keyboard is open (writes above are change-gated).
      if (kb && !sim) {
        loopRunning = true;
        rafId = requestAnimationFrame(measure);
      } else {
        loopRunning = false;
      }
    };

    const kick = () => {
      if (!loopRunning) measure();
    };
    const onFocusIn = (e) => {
      if (isTextTarget(e.target)) {
        textFocused = true;
        kick();
      }
    };
    const onFocusOut = (e) => {
      if (isTextTarget(e.target)) {
        textFocused = false;
        kick();
      }
    };

    vv.addEventListener("resize", kick);
    vv.addEventListener("scroll", kick);
    window.addEventListener("focusin", onFocusIn);
    window.addEventListener("focusout", onFocusOut);
    // Test harness: window.__aiWsSimulate({height, offsetTop, innerHeight})
    // drives the exact production code path with synthetic viewport
    // values; __aiWsSimulate(null) returns to the real visualViewport.
    window.__aiWsSimulate = (v) => {
      sim = v;
      if (v && typeof v.textFocused === "boolean") textFocused = v.textFocused;
      measure();
    };
    measure();

    return () => {
      cancelAnimationFrame(rafId);
      vv.removeEventListener("resize", kick);
      vv.removeEventListener("scroll", kick);
      window.removeEventListener("focusin", onFocusIn);
      window.removeEventListener("focusout", onFocusOut);
      delete window.__aiWsSimulate;
      root.style.removeProperty("--ai-vvh");
      root.style.removeProperty("--ai-vvoff");
      root.classList.remove("ai-kb-open");
      root.classList.remove("ai-ws-lock");
    };
  }, [active]);
}
